import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { inject } from '@angular/core';
import { AppConfig, buildApiUrl } from '@app/app.config';
import { computed, signal } from '@angular/core';
import { FeatureFlag, FeatureFlagsService } from '@app/core/feature-flags';
import { NotificationItem, NotificationResponse, Notifications } from './utils';
import { Observable, tap, catchError, throwError } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class NotificationService {
  private http = inject(HttpClient);
  private featureFlags = inject(FeatureFlagsService);

  private notificationSignal = signal<NotificationResponse | null>(null);
  private resumeNotificationSignal = signal<NotificationItem[]>([]);
  private pendingNotificationSignal = signal<NotificationItem[] | null>(null);

  public notifications = computed(() => this.notificationSignal());
  public resumeNotifications = computed(() => this.resumeNotificationSignal());
  public pendingNotifications = computed(() => this.pendingNotificationSignal());
  public notificationsForDisplay = computed(() => {
    const all = this.notificationSignal()?.notifications ?? [];
    let pendingIncluded = false;
    return all.filter(n => {
      if (n.typeNotification === Notifications.pendingPayment) {
        if (pendingIncluded) return false;
        pendingIncluded = true;
        return true;
      }
      return true;
    });
  });

  private applyResponse(response: NotificationResponse): void {
    this.notificationSignal.set(response);

    const list = response.notifications ?? [];
    const onlyResume = list.filter(n => n.typeNotification === Notifications.resume);
    this.resumeNotificationSignal.set(onlyResume);

    const onlyPending = list.filter(n => n.typeNotification === Notifications.pendingPayment);
    this.pendingNotificationSignal.set(onlyPending);
  }

  loadNotifications(): void {

    if(this.featureFlags.isDisabled(FeatureFlag.NOTIFICATIONS)) return;

    this.http
      .get<NotificationResponse>(buildApiUrl(AppConfig.getPolling), {
        withCredentials: true,
      })
      .subscribe((response: NotificationResponse) => {
        this.applyResponse(response);
      });
  }

  mergeIncoming(items: NotificationItem[]): void {
    
    if (this.featureFlags.isDisabled(FeatureFlag.NOTIFICATIONS)) return;

    if (!items || items.length === 0) return;

    const current = this.notificationSignal();
    const existing = current?.notifications ?? [];

    const newIds = new Set(items.map(item => item.id));
    const remaining = existing.filter(item => !newIds.has(item.id));
    const merged = [...items, ...remaining];

    const unreadCount = merged.filter(item => !item.isRead).length;

    const next: NotificationResponse = current
      ? { ...current, notifications: merged, unreadCount }
      : { notifications: merged, unreadCount, totalCount: merged.length, page: 1, pageSize: merged.length, hasNextPage: false };

    this.applyResponse(next);
  }
  
  private applyLocalMarkRead(id: number): void {
    const current = this.notificationSignal();
    if (!current) return;

    const merged = (current.notifications ?? []).map(item =>
      item.id === id ? { ...item, isRead: true, readAt: item.readAt } : item
    );
    const unreadCount = merged.filter(item => !item.isRead).length;
    const next: NotificationResponse = { ...current, notifications: merged, unreadCount };
    this.applyResponse(next);
  }

  private applyLocalMarkAllRead(): void {
    const current = this.notificationSignal();
    if (!current) return;

    const merged = (current.notifications ?? []).map(item => ({
      ...item,
      isRead: true,
      readAt: item.readAt
    }));
    const next: NotificationResponse = { ...current, notifications: merged, unreadCount: 0 };
    this.applyResponse(next);
  }

  markAllRead(): Observable<void> {
    this.applyLocalMarkAllRead();
    return this.http.post<void>(buildApiUrl(AppConfig.postMarkAllRead), {}, {
      withCredentials: true,
    }).pipe(
      tap(() => {}),
      catchError((err) => {
        this.loadNotifications();
        return throwError(() => err);
      })
    );
  }

  markRead(id: string): Observable<void> {
    return this.http.post<void>(buildApiUrl(AppConfig.postMarkReadId(id)), {}, {
      withCredentials: true,
    }).pipe(
      tap(() => this.applyLocalMarkRead(Number(id)))
    );
  }

  clear(): void {
    this.notificationSignal.set(null);
    this.resumeNotificationSignal.set([]);
    this.pendingNotificationSignal.set(null);
  }
}
